/**
 * /posvenda/assistencias/criar — Nova assistencia ao cliente.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiArrowLeft, FiSave } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import EntityHeader from "../../../components/EntityHeader";
import { useAuth } from "../../../context/AuthContext";
import {
  criarAssistencia,
  PRIORIDADES,
  TIPOS_ASSISTENCIA,
} from "../../../services/posvenda";
import { PageContainer } from "../../../styles/shared";

import {
  Card,
  ErrorMsg,
  Input,
  SecondaryButton,
  SectionTitle,
  Select,
  Textarea,
} from "../../Orcamentos/styles";


export default function AssistenciaCriar() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { request } = useAuth();

  const [pedidoId, setPedidoId] = useState(searchParams.get("pedido") || "");
  const [pedidos, setPedidos] = useState([]);
  const [carregandoPedidos, setCarregandoPedidos] = useState(false);
  const [tipo, setTipo] = useState("GARANTIA");
  const [prioridade, setPrioridade] = useState("MEDIA");
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    setCarregandoPedidos(true);
    request("/api/pedidos?pageSize=100")
      .then((p) => {
        const list = Array.isArray(p) ? p : p?.results || [];
        setPedidos(list);
      })
      .catch((err) => setError(err?.message || "Erro ao carregar pedidos."))
      .finally(() => setCarregandoPedidos(false));
  }, [request]);

  const handleCriar = useCallback(async () => {
    if (!pedidoId) throw new Error("Selecione um pedido.");
    if (!titulo.trim()) throw new Error("Título é obrigatório.");
    if (!descricao.trim()) throw new Error("Descrição é obrigatória.");
    const a = await criarAssistencia(request, {
      pedido_id: pedidoId, tipo, prioridade,
      titulo: titulo.trim(), descricao: descricao.trim(),
    });
    navigate(`/posvenda/assistencias/${a.id}`);
  }, [request, pedidoId, tipo, prioridade, titulo, descricao, navigate]);

  return (
    <PageContainer>
      <EntityHeader
        title="Nova assistência ao cliente"
        subtitle="Registrar demanda pós-entrega (ADR-0027)"
        actions={[{
          label: "Voltar",
          icon: <FiArrowLeft size={14} aria-hidden="true" />,
          onClick: () => navigate("/posvenda/assistencias"),
        }]}
      />

      {error && <ErrorMsg>{error}</ErrorMsg>}

      <Card>
        <SectionTitle>Dados</SectionTitle>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Pedido *</span>
            <Select value={pedidoId} onChange={(e) => setPedidoId(e.target.value)}
                    disabled={carregandoPedidos}>
              <option value="">
                {carregandoPedidos ? "Carregando..." : "Selecione um pedido"}
              </option>
              {pedidos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.numero} - {p.cliente_nome || ""}
                </option>
              ))}
            </Select>
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>Tipo *</span>
              <Select value={tipo} onChange={(e) => setTipo(e.target.value)}>
                {TIPOS_ASSISTENCIA.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Select>
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>Prioridade *</span>
              <Select value={prioridade} onChange={(e) => setPrioridade(e.target.value)}>
                {PRIORIDADES.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </Select>
            </label>
          </div>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Título *</span>
            <Input
              value={titulo} onChange={(e) => setTitulo(e.target.value)}
              maxLength={200} placeholder="Ex: Porta da cozinha empenou"
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Descrição *</span>
            <Textarea
              rows={5} value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Descreva o problema relatado pelo cliente em detalhes..."
            />
          </label>
        </div>
      </Card>

      <Card>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <SecondaryButton onClick={() => navigate("/posvenda/assistencias")}>
            Cancelar
          </SecondaryButton>
          <AsyncButton
            variant="primary"
            onClick={handleCriar}
            successMessage="Assistência aberta"
          >
            <FiSave size={14} /> Abrir assistência
          </AsyncButton>
        </div>
      </Card>
    </PageContainer>
  );
}
